// The reinsurance design system — every primitive the ten screens share.
//
// Built from the approved designs (2026-09-16). All ten pages are the same
// object: header + action buttons, a row of four KPI cards, a filter bar, a
// dense table, and a right-hand drawer that opens on the selected row. Writing
// that once means the tenth screen is a column list and a panel list, not
// another six hundred lines of layout.
//
// The app shell is NOT touched. These render inside the existing AppLayout —
// the shell redesign is a separate sprint, and mixing the two would put an
// unreviewed sidebar in front of a client.

import React, { useEffect, useRef, useState } from 'react';
import { formatDate } from '../regureStyles';

export const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Palette ──────────────────────────────────────────────────
export const C = {
  burgundy:  '#8a1538',
  burgundyDk:'#6d1029',
  ink:       '#111827',
  body:      '#374151',
  muted:     '#6b7280',
  faint:     '#9ca3af',
  border:    '#e5e7eb',
  hair:      '#f3f4f6',
  panel:     '#f9fafb',
  page:      '#ffffff',
  white:     '#ffffff',
  green:     '#15803d',
  greenBg:   '#dcfce7',
  red:       '#b91c1c',
  redBg:     '#fee2e2',
  amber:     '#b45309',
  amberBg:   '#fef3c7',
  blue:      '#1d4ed8',
  blueBg:    '#dbeafe',
  neutralBg: '#f1f5f9',
  selected:  '#fdf2f5',
  bar:       '#16a34a',
  barTrack:  '#eef2f7',
};

// ─── Money ────────────────────────────────────────────────────
// The number of decimal places belongs to the CURRENCY. BHD, KWD and OMR are
// three-decimal currencies; rendering "BHD 1,234.50" understates a balance by
// a factor of ten in the last place. Mirrors the backend's single table in
// services/genericOfferDocument.js.
export const CURRENCY_DECIMALS = {
  BHD: 3, KWD: 3, OMR: 3, JOD: 3, TND: 3, LYD: 3, IQD: 3,
  JPY: 0, KRW: 0, CLP: 0, ISK: 0, VND: 0,
};
const dp = (ccy) => CURRENCY_DECIMALS[String(ccy || '').toUpperCase()] ?? 2;

export const money = (n, ccy, { decimals } = {}) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const d = decimals ?? dp(ccy);
  const amt = Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
  return ccy ? `${ccy} ${amt}` : amt;
};

// Whole units — what the designs use in table cells: "USD 25,000,000".
export const money0 = (n, ccy) => money(n, ccy, { decimals: 0 });

// Abbreviated, for headline figures: "USD 482M".
export const moneyShort = (n, ccy) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const u = abs >= 1e9 ? ['B', 1e9] : abs >= 1e6 ? ['M', 1e6] : abs >= 1e3 ? ['K', 1e3] : null;
  if (!u) return money(v, ccy, { decimals: 0 });
  return `${ccy ? `${ccy} ` : ''}${(v / u[1]).toFixed(1).replace(/\.0$/, '')}${u[0]}`;
};

// A figure the API could not express in one currency comes back as
// { amount: null, currencies: [...] } rather than a sum of different units.
export const moneyMixed = (m) => {
  if (!m) return '—';
  if (m.amount !== null && m.amount !== undefined) return moneyShort(m.amount, m.currency);
  const list = m.currencies || [];
  // Only a genuinely MIXED scope gets the "n currencies" answer. One
  // currency and no amount is not a total this refuses to compute - it is
  // missing data. The dashboard shipped "Recoveries Due: 1 currencies"
  // because a controller spelled the figure `value` while the formatter
  // read `amount`, and this branch dressed that fault up as a decision.
  if (list.length < 2) return '—';
  return `${list.length} currencies`;
};

export const pct = (n, d = 0) =>
  (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : `${Number(n).toFixed(d)}%`);

// Delegates to the platform formatter. This was
// `new Date(v).toLocaleDateString('en-GB', …)`, which carried two bugs: no
// timeZone, so a date stored as midnight UTC rendered as the PREVIOUS day
// for every viewer west of Greenwich (a treaty incepting 1 Jan 2026 read
// "31 Dec 2025"); and a hardcoded locale, so a tenant set to MM/DD/YYYY got
// DD/MM anyway. formatDate pins date-only values to UTC and honours the
// tenant's format. One resolver for the whole platform, not two.
export const date = (v) => (v ? formatDate(v) : '—');
export const dateISO = (v) => { try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; } };
export const humanise = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ─── Status pill ──────────────────────────────────────────────
// One vocabulary for all ten screens. A status with no entry renders neutral
// rather than borrowing a colour that means something else — grey "unknown"
// is honest, green is a lie.
const TONES = {
  green:   { bg: C.greenBg,   fg: C.green },
  blue:    { bg: C.blueBg,    fg: C.blue },
  amber:   { bg: C.amberBg,   fg: C.amber },
  red:     { bg: C.redBg,     fg: C.red },
  neutral: { bg: C.neutralBg, fg: '#475569' },
};

export const STATUS_TONE = {
  active: 'green', bound: 'green', settled: 'green', clear: 'green', closed: 'green',
  renewed: 'green', on_track: 'green', validated: 'green', paid: 'green',
  ready_for_placement: 'green', firm: 'green', complete: 'green', open: 'green',
  quoted: 'blue', issued: 'blue', agreed: 'blue', in_progress: 'blue',
  in_review: 'blue', reviewing: 'blue', quotes_received: 'blue', indicative: 'blue',
  pending: 'amber', due_soon: 'amber', under_review: 'amber', limited: 'amber',
  awaiting_signature: 'amber', pending_docs: 'amber', awaiting_info: 'amber',
  more_info: 'amber', early_discussions: 'amber', draft: 'neutral',
  overdue: 'red', at_risk: 'red', disputed: 'red', flagged: 'red', urgent: 'red',
  blocked: 'red', lapsed: 'red', declined: 'red', failed: 'red',
  // EXPIRED IS AMBER, NOT RED.
  //
  // Red is "this is wrong and somebody must act on it now" — a sanctions hit,
  // a disputed balance, an overdue account. An expired KYC check is a piece of
  // housekeeping that has fallen out of date: it needs doing, it is not an
  // alarm, and colouring it the same as a sanctions flag teaches people to
  // read past the red. QA logged the distinction on 2026-09-21.
  expired: 'amber',
  // Compliance positions that mean "nobody has done this yet". Neutral, not
  // green: never screened is not the same as screened and clear, and the one
  // thing this pill must never do is imply a check that has not happened.
  not_started: 'neutral', not_screened: 'neutral',
  // `closed` on a capacity line means the market is shut to us — that is a
  // constraint, not an achievement, and it was rendering green next to
  // `open`, which is also green. Capacity uses its own key so the generic
  // `closed` (a closed file, which IS done) keeps its meaning.
  capacity_closed: 'neutral',
  run_off: 'neutral', written_off: 'neutral',
};

// Where a stored status and the word the market uses for it differ.
//
// The deal status `quoting` is displayed as "In market" on the dashboard and
// in the Submissions filter, because that is what a broker calls a risk that
// is out with reinsurers. The status control and every Pill said "Quoting",
// so the same state had two names in one product and QA reported the state as
// missing — it was there under the other word. One map, so a rename cannot
// land in two of the three places again.
//
// Statuses only. `humanise` stays the generic fallback for everything else.
export const STATUS_LABELS = {
  quoting: 'In market',
};
export const statusLabel = (s) => {
  const k = String(s || '').toLowerCase().replace(/\s+/g, '_');
  return STATUS_LABELS[k] || humanise(s);
};

export function Pill({ status, label, tone, size = 'md' }) {
  const key = String(status || '').toLowerCase().replace(/\s+/g, '_');
  const t = TONES[tone || STATUS_TONE[key] || 'neutral'];
  return (
    <span style={{
      display: 'inline-block', whiteSpace: 'nowrap',
      padding: size === 'sm' ? '2px 7px' : '3px 10px',
      borderRadius: 999, fontSize: size === 'sm' ? 10.5 : 11.5, fontWeight: 600,
      background: t.bg, color: t.fg,
    }}>
      {label || statusLabel(status) || '—'}
    </span>
  );
}

// ─── Icons ────────────────────────────────────────────────────
// Inline SVG, 20px, stroke 1.7 — matching the sidebar's existing set so the
// module does not introduce a second icon language.
const svg = (children, s = 20) => (
  <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke='currentColor'
    strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>{children}</svg>
);
export const Icons = {
  doc:    () => svg(<><path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' /><polyline points='14 2 14 8 20 8' /></>),
  shield: () => svg(<path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />),
  clock:  () => svg(<><circle cx='12' cy='12' r='9' /><polyline points='12 7 12 12 15 14' /></>),
  users:  () => svg(<><path d='M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' /><circle cx='9.5' cy='7' r='4' /><path d='M23 21v-2a4 4 0 0 0-3-3.87' /></>),
  db:     () => svg(<><ellipse cx='12' cy='5' rx='9' ry='3' /><path d='M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5' /><path d='M3 12c0 1.7 4 3 9 3s9-1.3 9-3' /></>),
  alert:  () => svg(<><path d='M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z' /><line x1='12' y1='9' x2='12' y2='13' /><line x1='12' y1='17' x2='12.01' y2='17' /></>),
  send:   () => svg(<><line x1='22' y1='2' x2='11' y2='13' /><polygon points='22 2 15 22 11 13 2 9 22 2' /></>),
  check:  (s = 20) => svg(<><circle cx='12' cy='12' r='9' /><polyline points='8.5 12.5 11 15 16 9.5' /></>, s),
  refresh:() => svg(<><polyline points='23 4 23 10 17 10' /><polyline points='1 20 1 14 7 14' /><path d='M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15' /></>),
  cal:    () => svg(<><rect x='3' y='4' width='18' height='18' rx='2' /><line x1='16' y1='2' x2='16' y2='6' /><line x1='8' y1='2' x2='8' y2='6' /><line x1='3' y1='10' x2='21' y2='10' /></>),
  chart:  () => svg(<><line x1='12' y1='20' x2='12' y2='10' /><line x1='18' y1='20' x2='18' y2='4' /><line x1='6' y1='20' x2='6' y2='14' /><line x1='3' y1='20' x2='21' y2='20' /></>),
  coins:  () => svg(<><ellipse cx='12' cy='6' rx='8' ry='3' /><path d='M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6' /><path d='M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6' /></>),
  clip:   () => svg(<path d='M21.4 11.05 12.25 20.2a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.67 3.67 0 1 1 5.18 5.18l-9.2 9.19a1.83 1.83 0 0 1-2.59-2.59l8.49-8.48' />),
  search: (s = 16) => svg(<><circle cx='11' cy='11' r='7' /><line x1='21' y1='21' x2='16.65' y2='16.65' /></>, s),
  x:      (s = 18) => svg(<><line x1='18' y1='6' x2='6' y2='18' /><line x1='6' y1='6' x2='18' y2='18' /></>, s),
  plus:   (s = 16) => svg(<><line x1='12' y1='5' x2='12' y2='19' /><line x1='5' y1='12' x2='19' y2='12' /></>, s),
  arrowR: (s = 14) => svg(<><line x1='5' y1='12' x2='19' y2='12' /><polyline points='12 5 19 12 12 19' /></>, s),
  up:     (s = 12) => svg(<polyline points='6 15 12 9 18 15' />, s),
  down:   (s = 12) => svg(<polyline points='6 9 12 15 18 9' />, s),
};

// ─── Page scaffolding ─────────────────────────────────────────
export const S = {
  page:  { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: C.page },
  body:  { flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' },
  main:  { flex: 1, minWidth: 0, overflowY: 'auto', padding: '24px 28px 40px' },
  h1:    { fontSize: 26, fontWeight: 700, color: C.ink, margin: 0, letterSpacing: '-0.02em' },
  sub:   { color: C.muted, margin: '5px 0 0', fontSize: 13.5 },
  card:  { background: C.white, border: `1px solid ${C.border}`, borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:    { textAlign: 'left', padding: '10px 12px', color: C.muted, fontWeight: 600, fontSize: 10.5,
           textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  td:    { padding: '13px 12px', borderBottom: `1px solid ${C.hair}`, color: C.body, verticalAlign: 'middle' },
  num:   { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
};

// One keyframes block for the whole module. Injected once, on first use, so
// the kit stays a single import with no stylesheet to remember to add.
let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  keyframesInjected = true;
  const el = document.createElement('style');
  el.setAttribute('data-reinsurance-kit', '');
  el.textContent = `
@keyframes reins-spin { to { transform: rotate(360deg) } }
@keyframes reins-drawer-in { from { transform: translateX(22px); opacity: 0 } to { transform: none; opacity: 1 } }
@keyframes reins-drawer-out { from { transform: none; opacity: 1 } to { transform: translateX(22px); opacity: 0 } }`;
  document.head.appendChild(el);
}

// A spinner, not the word "Loading". A blank cell that later fills with text
// reads as an empty list for as long as the request takes.
export function Spinner({ label = 'Loading', size = 18 }) {
  injectKeyframes();
  return (
    <div role='status' aria-live='polite'
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: C.muted }}>
      <span aria-hidden style={{
        width: size, height: size, borderRadius: '50%', display: 'inline-block',
        border: `2px solid ${C.border}`, borderTopColor: C.burgundy,
        animation: 'reins-spin 0.7s linear infinite',
      }} />
      <span style={{ fontSize: 13 }}>{label}…</span>
    </div>
  );
}

export function PrimaryButton({ children, onClick, icon = true, disabled }) {
  return (
    <button type='button' onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '9px 16px', borderRadius: 9, border: 'none',
      background: disabled ? C.faint : C.burgundy, color: '#fff',
      cursor: disabled ? 'default' : 'pointer', fontWeight: 600, fontSize: 13,
    }}>
      {icon && Icons.plus()}
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, icon, disabled }) {
  return (
    <button type='button' onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '9px 15px', borderRadius: 9, border: `1px solid ${C.border}`,
      background: '#fff', color: C.body, cursor: disabled ? 'default' : 'pointer',
      fontWeight: 600, fontSize: 13,
    }}>
      {icon}
      {children}
    </button>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={S.h1}>{title}</h1>
        {subtitle && <p style={S.sub}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// ─── KPI row ──────────────────────────────────────────────────
// Four cards, icon in a tinted square on the left, trend beneath. A null
// trend renders "no prior period", never 0% — "flat" and "nothing to compare
// with" are different facts and the dashboard must not conflate them.
export function Kpi({ icon, value, label, trend, comparison = 'vs. last month', tone = 'blue' }) {
  const tint = { blue: '#eff6ff', green: '#f0fdf4', amber: '#fffbeb', red: '#fef2f2', neutral: C.panel }[tone] || '#eff6ff';
  const fg   = { blue: C.blue, green: C.green, amber: C.amber, red: C.red, neutral: C.muted }[tone] || C.blue;
  const up = Number(trend) >= 0;
  return (
    <div style={{ ...S.card, padding: '18px 20px', display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
      <span style={{
        flexShrink: 0, width: 42, height: 42, borderRadius: 10, background: tint, color: fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 23, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
          {value}
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 3 }}>{label}</div>
        {trend === null || trend === undefined || Number.isNaN(Number(trend)) ? (
          <div style={{ fontSize: 11.5, color: C.faint, marginTop: 7 }}>no prior period</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 7 }}>
            <span style={{ color: up ? C.green : C.red, display: 'inline-flex' }}>
              {up ? Icons.up() : Icons.down()}
            </span>
            <span style={{ color: up ? C.green : C.red, fontSize: 12, fontWeight: 700 }}>
              {up ? '+' : ''}{Number(trend).toFixed(0)}%
            </span>
            <span style={{ color: C.faint, fontSize: 11.5 }}>{comparison}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function KpiRow({ children }) {
  return (
    <div style={{ display: 'grid', gap: 14, marginBottom: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
      {children}
    </div>
  );
}

// ─── Filter bar ───────────────────────────────────────────────
export function FilterBar({ search, onSearch, placeholder = 'Search…', filters = [], onFilter, values = {}, right }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, flex: '1 1 260px', maxWidth: 340,
        border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 12px', background: '#fff',
      }}>
        <span style={{ color: C.faint, display: 'inline-flex' }}>{Icons.search()}</span>
        <input
          value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder}
          style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, minWidth: 0, color: C.body, background: 'transparent' }}
        />
      </label>
      {filters.map(f => (f.type === 'daterange' ? (
        // A period, on the screens where a period is part of what the record
        // IS — an account's quarter, a renewal date, a loss date, a due date.
        // Deliberately not offered on the registers (treaties, counterparties,
        // submissions), which are searched by name and reference; a date
        // filter there is a control nobody touches.
        <span key={f.key} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: `1px solid ${C.border}`, borderRadius: 9, padding: '4px 9px', background: '#fff',
        }}>
          <span style={{ fontSize: 11.5, color: C.muted, whiteSpace: 'nowrap' }}>{f.label}</span>
          <input
            type='date' aria-label={`${f.label} from`} value={values[f.fromKey || 'from'] || ''}
            onChange={(e) => onFilter(f.fromKey || 'from', e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 12.5, color: C.body, background: 'transparent' }}
          />
          <span style={{ color: C.faint }}>–</span>
          <input
            type='date' aria-label={`${f.label} to`} value={values[f.toKey || 'to'] || ''}
            onChange={(e) => onFilter(f.toKey || 'to', e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 12.5, color: C.body, background: 'transparent' }}
          />
          {(values[f.fromKey || 'from'] || values[f.toKey || 'to']) && (
            <button
              type='button' aria-label={`Clear ${f.label}`} title='Clear'
              onClick={() => { onFilter(f.fromKey || 'from', ''); onFilter(f.toKey || 'to', ''); }}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.faint, padding: 0, lineHeight: 0 }}
            >
              {Icons.x(13)}
            </button>
          )}
        </span>
      ) : (
        <select
          key={f.key} value={values[f.key] ?? 'all'} onChange={(e) => onFilter(f.key, e.target.value)}
          aria-label={f.label}
          style={{ padding: '8px 11px', borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.body }}
        >
          {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )))}
      <span style={{ marginLeft: 'auto', fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap' }}>{right}</span>
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────
// A column is sortable when it names a stored field. `sortKey` says which one
// when the column key is not it; `sortable: false` opts a computed column out.
// Columns that render a derived value have nothing for the database to sort on,
// and a header that looks clickable and does nothing is worse than a plain one.
// `sortableFields` is the server's own list of orderable paths, from the list
// response. When it is present it is AUTHORITATIVE: a column is sortable only
// if the database can actually order by it. `sortable: false` and `sortKey`
// still work for a column that needs to opt out or point elsewhere, but nobody
// has to remember them for the common case any more — which is what went
// wrong, because they were used once across the whole module and every
// composite column advertised a sort the server threw away.
//
// A null `sortableFields` means the server did not say (an older response, or
// a table not on this factory), so fall back to the previous behaviour rather
// than silently disabling every header.
const sortKeyOf = (c, sortableFields) => {
  if (c.sortable === false) return null;
  const key = c.sortKey || c.key || null;
  if (!key) return null;
  if (Array.isArray(sortableFields) && !sortableFields.includes(key)) return null;
  return key;
};

export function DataTable({
  columns, rows, selectedId, onSelect, empty = 'Nothing to show.', loading,
  sort, onSort, sortableFields = null,
}) {
  const active = String(sort || '');
  const activeField = active.startsWith('-') ? active.slice(1) : active;
  const activeDir = active.startsWith('-') ? 'desc' : 'asc';
  const toggle = (key) => {
    if (!onSort) return;
    // First click sorts ascending; clicking the active column flips it.
    onSort(activeField === key && activeDir === 'asc' ? `-${key}` : key);
  };

  return (
    <div style={{ ...S.card, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 38 }} aria-label='Select' />
              {columns.map(c => {
                const key = sortKeyOf(c, sortableFields);
                const on = key && activeField === key;
                if (!key || !onSort) {
                  return <th key={c.key} style={{ ...S.th, ...(c.numeric ? S.num : null), width: c.width }}>{c.label}</th>;
                }
                return (
                  <th key={c.key}
                    style={{ ...S.th, ...(c.numeric ? S.num : null), width: c.width, padding: 0 }}
                    aria-sort={on ? (activeDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <button type='button' onClick={() => toggle(key)}
                      title={`Sort by ${c.label}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                        justifyContent: c.numeric ? 'flex-end' : 'flex-start',
                        background: 'none', border: 'none', cursor: 'pointer', font: 'inherit',
                        color: on ? C.ink : 'inherit', letterSpacing: 'inherit',
                        textTransform: 'inherit', padding: '10px 12px',
                      }}>
                      {c.label}
                      <span aria-hidden style={{ fontSize: 9, lineHeight: 1, opacity: on ? 1 : 0.28 }}>
                        {on ? (activeDir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} style={{ ...S.td, padding: '40px 0' }}><Spinner label='Loading' /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} style={{ ...S.td, textAlign: 'center', color: C.muted, padding: '40px 0' }}>{empty}</td></tr>
            ) : rows.map((row) => {
              const on = String(row._id) === String(selectedId);
              return (
                <tr
                  key={row._id}
                  onClick={() => onSelect(on ? null : row)}
                  style={{ cursor: 'pointer', background: on ? C.selected : undefined }}
                >
                  <td style={{ ...S.td, paddingRight: 0 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: 4,
                      border: `1.5px solid ${on ? C.burgundy : '#cbd5e1'}`,
                      background: on ? C.burgundy : '#fff', color: '#fff',
                    }}>
                      {on && (
                        <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='4' strokeLinecap='round' strokeLinejoin='round'>
                          <polyline points='20 6 9 17 4 12' />
                        </svg>
                      )}
                    </span>
                  </td>
                  {columns.map(c => (
                    <td key={c.key} style={{ ...S.td, ...(c.numeric ? S.num : null) }}>
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A two-line table cell: a strong primary line and a muted secondary.
export function TwoLine({ primary, secondary }) {
  return (
    <span>
      <span style={{ display: 'block', fontWeight: 600, color: C.ink }}>{primary ?? '—'}</span>
      {secondary ? <span style={{ display: 'block', fontSize: 11.5, color: C.muted, marginTop: 2 }}>{secondary}</span> : null}
    </span>
  );
}

// ─── Progress ─────────────────────────────────────────────────
export function Bar({ percent, width = '100%', tone }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const colour = tone || (p >= 90 ? C.bar : p >= 50 ? '#f59e0b' : '#ef4444');
  return (
    <span style={{ display: 'inline-block', width, height: 6, borderRadius: 999, background: C.barTrack, overflow: 'hidden', verticalAlign: 'middle' }}>
      <span style={{ display: 'block', width: `${p}%`, height: '100%', background: colour, borderRadius: 999 }} />
    </span>
  );
}

// "8/10" over a bar — the docs-progress cell used on several screens.
export function ProgressCell({ done, total }) {
  const p = total ? (done / total) * 100 : 0;
  return (
    <span style={{ display: 'inline-block', minWidth: 74 }}>
      <span style={{ fontSize: 12, color: C.body, fontVariantNumeric: 'tabular-nums' }}>{done}/{total}</span>
      <span style={{ display: 'block', marginTop: 4 }}><Bar percent={p} /></span>
    </span>
  );
}

// ─── Drawer ───────────────────────────────────────────────────
// The right-hand panel every screen opens on a selected row. Scrolls
// independently of the table so a long record never pushes the list around.
const EXIT_MS = 170;

export function Drawer({ open, onClose, eyebrowTitle, badge, lines = [], tabs = [], tab, onTab, children }) {
  const ref = useRef(null);
  // Escape closes. A panel you cannot dismiss from the keyboard is a trap for
  // anyone not using a mouse.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  useEffect(() => { if (open && ref.current) ref.current.scrollTop = 0; }, [open, eyebrowTitle]);

  // Clicking away closes it, the same as Escape.
  //
  // This panel is an inline column beside the table, NOT a modal over it, so
  // there is no backdrop to catch the click — which is exactly why QA found
  // "clicking outside does nothing" on all ten screens. Listening on the
  // document and ignoring clicks inside the panel gives the expected
  // behaviour without turning the layout into an overlay.
  //
  // `mousedown`, not `click`: a click whose press began inside the panel and
  // released outside (a text selection dragged past the edge) must not close
  // it and lose the selection.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const el = ref.current;
      if (!el || el.contains(e.target)) return;
      // A row click lands outside the panel and is what MOVES the selection;
      // closing on it would fight the table. Anything inside the results
      // table is the table's business.
      if (e.target.closest && e.target.closest('table')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose]);

  // CLOSING HAS TO BE ANIMATED TOO.
  //
  // `if (!open) return null` unmounted the panel on the same frame the close
  // was clicked, so there was an entrance and nothing on the way out — which
  // is what QA logged as "no animation": half of it genuinely was not there.
  // The panel is held mounted for the length of the exit, then dropped.
  //
  // `leaving` is state rather than a ref because the render has to change; the
  // timer is cleared on unmount so a drawer closed and reopened quickly cannot
  // be torn down by the previous close's timeout.
  const [leaving, setLeaving] = useState(false);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      setLeaving(true);
      const t = setTimeout(() => setLeaving(false), EXIT_MS);
      wasOpen.current = open;
      return () => clearTimeout(t);
    }
    wasOpen.current = open;
    return undefined;
  }, [open]);

  if (!open && !leaving) return null;
  injectKeyframes();
  return (
    <aside
      ref={ref}
      style={{
        width: 388, flexShrink: 0, borderLeft: `1px solid ${C.border}`,
        background: C.white, overflowY: 'auto', padding: '22px 22px 40px',
        // 22px over 190ms reads as movement; the previous 14px/160ms was close
        // enough to instant that QA recorded it as none at all.
        animation: open
          ? 'reins-drawer-in 190ms ease-out'
          : `reins-drawer-out ${EXIT_MS}ms ease-in forwards`,
        // A panel on its way out must not accept clicks or keep focus.
        pointerEvents: open ? undefined : 'none',
      }}
      aria-label='Record detail'
      aria-hidden={open ? undefined : true}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>{eyebrowTitle}</h2>
            {badge}
          </div>
          {lines.map((l, i) => (
            <div key={i} style={{ fontSize: 12.5, color: i === 0 ? C.body : C.muted, marginTop: i === 0 ? 7 : 3 }}>{l}</div>
          ))}
        </div>
        <button type='button' onClick={onClose} aria-label='Close'
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.faint, padding: 2, flexShrink: 0 }}>
          {Icons.x()}
        </button>
      </div>

      {tabs.length > 0 && (
        <div style={{ display: 'flex', gap: 18, margin: '18px 0 6px', borderBottom: `1px solid ${C.border}` }}>
          {tabs.map(t => (
            <button key={t} type='button' onClick={() => onTab(t)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 9px',
                fontSize: 13, fontWeight: tab === t ? 700 : 500,
                color: tab === t ? C.ink : C.muted,
                borderBottom: tab === t ? `2px solid ${C.burgundy}` : '2px solid transparent',
                marginBottom: -1,
              }}>
              {t}
            </button>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </aside>
  );
}

// ─── Drawer panels ────────────────────────────────────────────
export function Panel({ title, action, children, tone }) {
  return (
    <section style={{
      border: `1px solid ${tone === 'quiet' ? C.hair : C.border}`,
      borderRadius: 10, padding: '14px 15px', background: tone === 'quiet' ? C.panel : '#fff',
    }}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 11 }}>
          {title ? <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: C.ink }}>{title}</h3> : <span />}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

// Label on the left, value on the right — the Key Information shape.
export function Row({ label, value, strong }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14, padding: '5px 0' }}>
      <span style={{ fontSize: 12.5, color: C.muted, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: 12.5, color: C.ink, fontWeight: strong ? 700 : 500, textAlign: 'right',
        minWidth: 0, wordBreak: 'break-word',
      }}>
        {value === null || value === undefined || value === '' ? <span style={{ color: C.faint }}>—</span> : value}
      </span>
    </div>
  );
}

// A checklist with a completion bar — renewals, documents, validation.
export function Checklist({ items = [], countLabel }) {
  const done = items.filter(i => i.completed || i.uploaded || i.status === 'valid' || i.status === 'approved' || i.status === 'completed').length;
  const p = items.length ? (done / items.length) * 100 : 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ fontSize: 12.5, color: C.body, fontWeight: 600 }}>
          {countLabel || `${done} of ${items.length} completed`}
        </span>
        <span style={{ fontSize: 12, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{Math.round(p)}%</span>
      </div>
      <Bar percent={p} tone={C.bar} />
      <div style={{ marginTop: 11 }}>
        {items.map((it, i) => {
          const ok = it.completed || it.uploaded || ['valid', 'approved', 'completed'].includes(it.status);
          const bad = it.status === 'expired';
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '5px 0' }}>
              <span style={{
                flexShrink: 0, marginTop: 1, width: 15, height: 15, borderRadius: 999,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: bad ? C.redBg : ok ? C.greenBg : 'transparent',
                border: ok || bad ? 'none' : `1.5px solid ${C.border}`,
                color: bad ? C.red : C.green, fontSize: 10, fontWeight: 800,
              }}>
                {bad ? '!' : ok ? '✓' : ''}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: ok ? C.body : C.muted }}>{it.label}</span>
              {it.right && <span style={{ fontSize: 11.5, color: bad ? C.red : C.muted, flexShrink: 0 }}>{it.right}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The Next Action block that closes every drawer in the designs.
export function NextAction({ label, detail, tone = 'amber', button, onButton }) {
  if (!label) return null;
  const fg = { amber: C.amber, green: C.green, blue: C.blue, red: C.red }[tone] || C.amber;
  const bg = { amber: C.amberBg, green: C.greenBg, blue: C.blueBg, red: C.redBg }[tone] || C.amberBg;
  return (
    <Panel title='Next Action'>
      <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
        <span style={{
          flexShrink: 0, width: 30, height: 30, borderRadius: 999, background: bg, color: fg,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{Icons.clock()}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{label}</div>
          {detail && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{detail}</div>}
        </div>
      </div>
      {button && (
        <div style={{ marginTop: 12 }}>
          <PrimaryButton icon={false} onClick={onButton}>{button}</PrimaryButton>
        </div>
      )}
    </Panel>
  );
}

// A small table inside a drawer panel.
export function MiniTable({ head = [], rows = [], empty = '—' }) {
  if (!rows.length) return <div style={{ fontSize: 12.5, color: C.faint }}>{empty}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} style={{
                textAlign: i === 0 ? 'left' : 'right', padding: '0 0 7px', color: C.muted,
                fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((cell, ci) => (
                <td key={ci} style={{
                  padding: '6px 0', textAlign: ci === 0 ? 'left' : 'right',
                  color: ci === 0 ? C.body : C.ink, borderTop: ri ? `1px solid ${C.hair}` : 'none',
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ViewAllLink({ children, onClick }) {
  return (
    <button type='button' onClick={onClick} style={{
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
      color: C.blue, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      {children} {Icons.arrowR(12)}
    </button>
  );
}
